import _ from 'lodash';

export function removeNull<T extends Object>(obj: T): T {
  return <T>Object.fromEntries(
    Object.entries(obj)
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      .filter(([k, v]) => v != null)
      .map(([k, v]) => [k, v === Object(v) ? removeNull(v) : v])
  );
}

export function removeKey<T extends Object>(obj: T, key: string | string[]): T {
  if (Array.isArray(key)) {
    return <T>Object.fromEntries(
      Object.entries(obj)
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        .filter(([k]) => key.indexOf(k) <= -1)
        .map(([k, v]) => [k, v === Object(v) && !Array.isArray(v) ? removeKey(v, key) : v])
    );
  }
  return <T>Object.fromEntries(
    Object.entries(obj)
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      .filter(([k]) => k !== key)
      .map(([k, v]) => [k, v === Object(v) && !Array.isArray(v) ? removeKey(v, key) : v])
  );
}

export function removeKeyArray<T extends Object>(obj: T[], key: string): T[] {
  return obj.map<T>((v) => removeKey(v, key));
}

export function getPaths(obj: any, parentKey?: string) {
  let result: string[];
  if (_.isArray(obj)) {
    let idx = -1;
    result = _.flatMap(obj, (item) => {
      idx += 1;
      return getPaths(item, (parentKey ? `${parentKey}.` : '') + idx);
    });
  } else if (_.isPlainObject(obj)) {
    result = _.flatMap(_.keys(obj), (key) =>
      _.map(getPaths(obj[key], key), (subkey) => (parentKey ? `${parentKey}.` : '') + subkey)
    );
  } else {
    result = [parentKey || ''];
  }
  return result;
}

//
const addToObjectKey = (
  obj: {
    [key: string]: number;
  },
  key: string,
  add: number
) => {
  if (obj[key] === undefined) {
    obj[key] = add;
  } else {
    obj[key] += add;
  }
};
export function nullateEmpty(
  obj: Object,
  combineDatePrefix: string | false = false,
  intoDatePrefix: string | false = false
): Object {
  const returnObj = Object.fromEntries(
    Object.entries(obj)
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      .map(([k, v]) => [
        k,
        v instanceof Date
          ? v
          : v === Object(v)
          ? nullateEmpty(v)
          : v === '' || v === undefined
          ? null
          : v,
      ])
  );

  if (combineDatePrefix && intoDatePrefix) {
    Object.entries(returnObj).forEach(([k, v]) => {
      if (k.startsWith(combineDatePrefix) && v instanceof Date) {
        if (
          returnObj[k.replace(combineDatePrefix, intoDatePrefix)] &&
          returnObj[k.replace(combineDatePrefix, intoDatePrefix)] instanceof Date
        ) {
          const currentDate = returnObj[k.replace(combineDatePrefix, intoDatePrefix)];
          currentDate.setHours(v.getHours(), v.getMinutes(), v.getSeconds(), v.getMilliseconds());
          returnObj[k.replace(combineDatePrefix, intoDatePrefix)] = currentDate;
          delete returnObj[k];
        }
      }
    });
  }

  return returnObj;
}

export function createDateValues<T extends Object | undefined | null>(
  obj: T,
  prefix = 'date',
  replaceForHours: string | false = false
): T | undefined | null {
  if (!obj) {
    return undefined;
  }
  const newObj = Object.fromEntries(
    Object.entries(obj).map(([k, v]) => {
      if (!_.isEmpty(v) && k.startsWith(prefix)) {
        return [k, new Date(v)];
      } else if (v === Object(v)) {
        return [k, createDateValues(v, prefix)];
      } else if (v === '' || v === undefined) {
        return [k, null];
      } else {
        return [k, v];
      }
    })
  );

  if (replaceForHours !== false) {
    Object.entries(obj).forEach(([k, v]) => {
      if (!_.isEmpty(v) && k.startsWith(prefix)) {
        newObj[k.replace(prefix, replaceForHours)] = new Date(v);
      }
    });
  }

  return newObj;
}

export function startsWithOneOf(str: string, prefixes: string[]) {
  return prefixes.some((prefix) => str.startsWith(prefix));
}

export function createTimeValues<T extends Object | undefined | null>(
  obj: T,
  prefix = ['heure', 'temps']
): T | undefined | null {
  if (!obj) {
    return undefined;
  }
  const newObj = Object.fromEntries(
    Object.entries(obj).map(([k, v]) => {
      if (!_.isEmpty(v) && startsWithOneOf(k, prefix)) {
        const date = new Date();
        const split = v.split(':');
        date.setHours(parseInt(split[0], 10), parseInt(split[1], 10), 0);
        return [k, date];
      } else if (v === Object(v)) {
        return [k, createTimeValues(v, prefix)];
      } else if (v === '' || v === undefined) {
        return [k, null];
      } else {
        return [k, v];
      }
    })
  );

  return newObj;
}

export default {
  createDateValues,
  createTimeValues,
  addToObjectKey,
  nullateEmpty,
  removeNull,
  removeKey,
  removeKeyArray,
  getPaths,
};
